import { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { X, Plus, Send, Loader2, Paperclip, FileText, Upload, FolderOpen } from "lucide-react";
import { useSendLeadEmail } from "@/hooks/useSendLeadEmail";
import { AttachmentPicker } from "@/components/AttachmentPicker";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Attachment {
  id?: string; // Document ID if from vault
  filename: string;
  content?: string; // Base64 content for new uploads
  content_type: string;
  size: number;
  file_path?: string; // Storage path if from vault
}

interface ComposeEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  defaultTo?: string;
  defaultSubject?: string;
  replyToEmailId?: string;
  originalBody?: string;
}

const MAX_ATTACHMENTS = 10;
const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25MB

export const ComposeEmailDialog = ({
  open,
  onOpenChange,
  leadId,
  defaultTo = "",
  defaultSubject = "",
  replyToEmailId,
  originalBody,
}: ComposeEmailDialogProps) => {
  const [to, setTo] = useState<string[]>([]);
  const [toInput, setToInput] = useState("");
  const [cc, setCc] = useState<string[]>([]);
  const [ccInput, setCcInput] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachmentPickerOpen, setAttachmentPickerOpen] = useState(false);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);

  const sendEmail = useSendLeadEmail();
  const { toast } = useToast();

  // Initialize form when dialog opens
  useEffect(() => {
    if (open) {
      setTo(defaultTo ? [defaultTo] : []);
      setToInput("");
      setCc([]);
      setCcInput("");
      setShowCc(false);
      setSubject(defaultSubject);
      setAttachments([]);
      
      // If replying, include quoted original message
      if (originalBody && replyToEmailId) {
        setBody(`\n\n---\n${originalBody}`);
      } else {
        setBody("");
      }
    }
  }, [open, defaultTo, defaultSubject, originalBody, replyToEmailId]);

  const totalAttachmentSize = attachments.reduce((sum, att) => sum + att.size, 0);

  const addEmail = (
    email: string,
    list: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>,
    setInput: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const trimmed = email.trim();
    if (trimmed && !list.includes(trimmed) && trimmed.includes("@")) {
      setList([...list, trimmed]);
      setInput("");
    }
  };

  const removeEmail = (
    email: string,
    list: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setList(list.filter((e) => e !== email));
  };

  const handleKeyDown = (
    e: React.KeyboardEvent,
    input: string,
    list: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>,
    setInput: React.Dispatch<React.SetStateAction<string>>
  ) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmail(input, list, setList, setInput);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleVaultSelect = async (documents: Array<{
    id: string;
    file_name: string;
    file_type: string | null;
    file_size: number | null;
    file_path: string;
  }>) => {
    const newAttachments: Attachment[] = documents.map((doc) => ({
      id: doc.id,
      filename: doc.file_name,
      content_type: doc.file_type || "application/octet-stream",
      size: doc.file_size || 0,
      file_path: doc.file_path,
    }));

    const combined = [...attachments, ...newAttachments];
    
    if (combined.length > MAX_ATTACHMENTS) {
      toast({
        title: "Too many attachments",
        description: `Maximum ${MAX_ATTACHMENTS} attachments allowed.`,
        variant: "destructive",
      });
      return;
    }

    const totalSize = combined.reduce((sum, att) => sum + att.size, 0);
    if (totalSize > MAX_TOTAL_SIZE) {
      toast({
        title: "Attachments too large",
        description: "Total attachment size cannot exceed 25MB.",
        variant: "destructive",
      });
      return;
    }

    setAttachments(combined);
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (attachments.length + acceptedFiles.length > MAX_ATTACHMENTS) {
        toast({
          title: "Too many attachments",
          description: `Maximum ${MAX_ATTACHMENTS} attachments allowed.`,
          variant: "destructive",
        });
        return;
      }

      const newTotalSize =
        totalAttachmentSize +
        acceptedFiles.reduce((sum, file) => sum + file.size, 0);

      if (newTotalSize > MAX_TOTAL_SIZE) {
        toast({
          title: "Attachments too large",
          description: "Total attachment size cannot exceed 25MB.",
          variant: "destructive",
        });
        return;
      }

      setIsUploadingAttachments(true);

      try {
        const newAttachments: Attachment[] = await Promise.all(
          acceptedFiles.map(async (file) => {
            // Convert file to base64
            const base64 = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                // Remove the data URL prefix
                const base64Data = result.split(",")[1];
                resolve(base64Data);
              };
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });

            return {
              filename: file.name,
              content: base64,
              content_type: file.type || "application/octet-stream",
              size: file.size,
            };
          })
        );

        setAttachments((prev) => [...prev, ...newAttachments]);
      } catch (error) {
        toast({
          title: "Upload failed",
          description: "Failed to process attachment files.",
          variant: "destructive",
        });
      } finally {
        setIsUploadingAttachments(false);
      }
    },
    [attachments.length, totalAttachmentSize, toast]
  );

  const { getRootProps, getInputProps, isDragActive, open: openFileDialog } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
  });

  const handleSend = async () => {
    if (to.length === 0) return;

    // Process vault attachments - fetch content
    const processedAttachments = await Promise.all(
      attachments.map(async (att) => {
        if (att.file_path && !att.content) {
          // Fetch from storage
          try {
            const { data, error } = await supabase.storage
              .from("documents")
              .download(att.file_path);

            if (error) throw error;

            const base64 = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                const base64Data = result.split(",")[1];
                resolve(base64Data);
              };
              reader.onerror = reject;
              reader.readAsDataURL(data);
            });

            return {
              filename: att.filename,
              content: base64,
              content_type: att.content_type,
            };
          } catch (error) {
            console.error("Failed to fetch attachment:", att.filename, error);
            return null;
          }
        } else if (att.content) {
          return {
            filename: att.filename,
            content: att.content,
            content_type: att.content_type,
          };
        }
        return null;
      })
    );

    const validAttachments = processedAttachments.filter(Boolean);

    await sendEmail.mutateAsync({
      leadId,
      to,
      cc: cc.length > 0 ? cc : undefined,
      subject,
      bodyText: body,
      bodyHtml: `<div style="white-space: pre-wrap;">${body.replace(/\n/g, "<br>")}</div>`,
      replyToEmailId,
      attachments: validAttachments.length > 0 ? validAttachments : undefined,
    });

    onOpenChange(false);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {replyToEmailId ? "Reply to Email" : "Compose Email"}
            </DialogTitle>
          </DialogHeader>

          <div {...getRootProps()} className={`space-y-4 py-4 ${isDragActive ? "opacity-50" : ""}`}>
            <input {...getInputProps()} />
            
            {isDragActive && (
              <div className="absolute inset-0 bg-primary/10 border-2 border-dashed border-primary rounded-lg flex items-center justify-center z-10">
                <div className="text-center">
                  <Upload className="h-8 w-8 mx-auto text-primary mb-2" />
                  <p className="text-sm font-medium">Drop files to attach</p>
                </div>
              </div>
            )}

            {/* To Field */}
            <div className="space-y-2">
              <Label htmlFor="to">To</Label>
              <div className="flex flex-wrap gap-1 p-2 border rounded-md min-h-[42px]">
                {to.map((email) => (
                  <Badge key={email} variant="secondary" className="gap-1">
                    {email}
                    <button
                      type="button"
                      onClick={() => removeEmail(email, to, setTo)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                <Input
                  id="to"
                  value={toInput}
                  onChange={(e) => setToInput(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, toInput, to, setTo, setToInput)}
                  onBlur={() => addEmail(toInput, to, setTo, setToInput)}
                  placeholder={to.length === 0 ? "Add recipient..." : ""}
                  className="flex-1 min-w-[150px] border-0 p-0 h-auto focus-visible:ring-0"
                />
              </div>
            </div>

            {/* CC Field (toggleable) */}
            {!showCc ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowCc(true)}
                className="text-muted-foreground"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add CC
              </Button>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="cc">CC</Label>
                <div className="flex flex-wrap gap-1 p-2 border rounded-md min-h-[42px]">
                  {cc.map((email) => (
                    <Badge key={email} variant="secondary" className="gap-1">
                      {email}
                      <button
                        type="button"
                        onClick={() => removeEmail(email, cc, setCc)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  <Input
                    id="cc"
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, ccInput, cc, setCc, setCcInput)}
                    onBlur={() => addEmail(ccInput, cc, setCc, setCcInput)}
                    placeholder={cc.length === 0 ? "Add CC recipients..." : ""}
                    className="flex-1 min-w-[150px] border-0 p-0 h-auto focus-visible:ring-0"
                  />
                </div>
              </div>
            )}

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject..."
              />
            </div>

            {/* Body */}
            <div className="space-y-2">
              <Label htmlFor="body">Message</Label>
              <Textarea
                id="body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write your message..."
                className="min-h-[200px] resize-y"
              />
            </div>

            {/* Attachments */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Attachments</Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={
                        attachments.length >= MAX_ATTACHMENTS ||
                        isUploadingAttachments
                      }
                    >
                      {isUploadingAttachments ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Paperclip className="h-4 w-4 mr-2" />
                      )}
                      Attach
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setAttachmentPickerOpen(true)}>
                      <FolderOpen className="h-4 w-4 mr-2" />
                      From Document Vault
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={openFileDialog}>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload New File
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-muted/30">
                  {attachments.map((att, index) => (
                    <Badge
                      key={`${att.filename}-${index}`}
                      variant="secondary"
                      className="gap-2 py-1.5"
                    >
                      <FileText className="h-3 w-3" />
                      <span className="truncate max-w-[150px]">{att.filename}</span>
                      <span className="text-muted-foreground text-xs">
                        ({formatFileSize(att.size)})
                      </span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {attachments.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {attachments.length}/{MAX_ATTACHMENTS} files •{" "}
                  {formatFileSize(totalAttachmentSize)}/25MB
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={sendEmail.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={to.length === 0 || sendEmail.isPending}
            >
              {sendEmail.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attachment Picker */}
      <AttachmentPicker
        open={attachmentPickerOpen}
        onOpenChange={setAttachmentPickerOpen}
        leadId={leadId}
        onSelect={handleVaultSelect}
        excludeIds={attachments.filter((a) => a.id).map((a) => a.id!)}
      />
    </>
  );
};
